import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, PaletteMode } from '@mui/material';

// Light theme configuration
const lightPalette = {
  mode: 'light' as PaletteMode,
  primary: {
    main: '#00d4ff',
    light: '#5dffff',
    dark: '#00a1cc',
  },
  secondary: {
    main: '#ff0080',
    light: '#ff5cac',
    dark: '#c90058',
  },
  background: {
    default: '#fefce8',
    paper: '#fefce8',
  },
  text: {
    primary: '#333',
    secondary: 'rgba(51, 51, 51, 0.7)',
  },
  success: {
    main: '#00ff88',
    light: '#5dffaa',
    dark: '#00cc55',
  },
  error: {
    main: '#ff3366',
    light: '#ff6699',
    dark: '#cc0033',
  },
  info: {
    main: '#00d4ff',
    light: '#66e0ff',
    dark: '#0099cc',
  },
  warning: {
    main: '#ffaa00',
    light: '#ffcc66',
    dark: '#cc8800',
  },
};

// Dark theme configuration
const darkPalette = {
  mode: 'dark' as PaletteMode,
  primary: {
    main: '#00d4ff',
    light: '#5dffff',
    dark: '#00a1cc',
  },
  secondary: {
    main: '#ff0080',
    light: '#ff5cac',
    dark: '#c90058',
  },
  background: {
    default: '#0a0f1a',
    paper: '#111827',
  },
  text: {
    primary: '#f0f4f8',
    secondary: 'rgba(240, 244, 248, 0.7)',
  },
  success: {
    main: '#00ff88',
    light: '#5dffaa',
    dark: '#00cc55',
  },
  error: {
    main: '#ff3366',
    light: '#ff6699',
    dark: '#cc0033',
  },
  info: {
    main: '#00d4ff',
    light: '#66e0ff',
    dark: '#0099cc',
  },
  warning: {
    main: '#ffaa00',
    light: '#ffcc66',
    dark: '#cc8800',
  },
};

const getDesignTokens = (mode: PaletteMode) => ({
  palette: mode === 'light' ? lightPalette : darkPalette,
  typography: {
    fontFamily:
      '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      letterSpacing: '-0.02em',
      color: mode === 'light' ? '#00a1cc' : '#00d4ff',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.75rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2.25rem',
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
         body: {
           backgroundColor: mode === 'light' ? '#fefce8' : '#0a0f1a',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 212, 255, 0.3)',
            borderRadius: 2,
            '&:hover': {
              background: 'rgba(0, 212, 255, 0.5)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 600,
          borderRadius: 2,
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative' as const,
          overflow: 'hidden' as const,
        },
        contained: {
          boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 30px rgba(0, 212, 255, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderWidth: '2px',
          backdropFilter: 'blur(10px)',
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
     root: {
       backgroundImage: 'none',
       backgroundColor: mode === 'light' ? '#fefce8' : '#111827',
       backdropFilter: 'blur(20px)',
           border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
           '&:hover': {
             border: '1px solid rgba(0, 212, 255, 0.2)',
             boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)',
           },
         },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
           backgroundColor: mode === 'light' ? '#fefce8' : '#111827',
          backdropFilter: 'blur(20px)',
          border: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            border: '1px solid rgba(0, 212, 255, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 212, 255, 0.15)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            '& fieldset': {
              borderColor: mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 212, 255, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00d4ff',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 212, 255, 0.08)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          fontWeight: 600,
          color: mode === 'light' ? '#333' : '#f0f4f8',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
     root: {
       backgroundColor: mode === 'light' ? '#fefce8' : '#0a0f1a',
       backdropFilter: 'blur(20px)',
       borderBottom: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: mode === 'light' ? '0 4px 30px rgba(0, 0, 0, 0.1)' : '0 4px 30px rgba(0, 212, 255, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: mode === 'light' ? '#fefce8' : '#0a0f1a',
          backdropFilter: 'blur(20px)',
          borderRight: mode === 'light' ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<PaletteMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as PaletteMode) || 'light';
  });

    useEffect(() => {
    localStorage.setItem('themeMode', mode);
    // Update body background color and data attribute for CSS
    document.body.setAttribute('data-theme', mode);
    document.body.style.backgroundColor = mode === 'light' ? '#fefce8' : '#0a0f1a';
    document.body.style.color = mode === 'light' ? '#333' : '#f0f4f8';
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;